import React, { useState, useEffect } from "react";
import TextField from "../customComponents/customTextField";
import { Snackbar, Alert, Dialog, DialogTitle, Button, DialogActions } from "@mui/material";
import { useCreateTenantUpdateMutation } from "../../api/tenantApi";
import { genericHeaders, serviceUrl } from "../../utils/apiConfig";
import Loader from "../../Components/customComponents/customLoader";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../../css/countrycodedropdown.css";
import { validateMobileNumber } from "../../utils";
import axios from "axios";
import Select from "../customComponents/customSelect";

const emptyProxyAdmin = () => ({
  name: "",
  emailId: "",
  phoneNumber: "",
});

const emptyProxyAdminError = () => ({
  name: "",
  emailId: "",
  phoneNumber: "",
});

const TenantForm = () => {
  const [loading, setLoading] = useState(false);
  const [names, setNames] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState({
    open: false,
    error: false,
    message: "",
  });
  const [formData, setFormData] = useState({
    tenantName: "",
    description: "",
    email: "",
    tenantCountry: "",
    phoneNumber: "",
    logoUrl: "",
    createdDate: "",
    address: "",
  });
  const [error, setError] = useState({
    tenantName: "",
    email: "",
    country: "",
    phoneNumber: "",
    address: "",
  });

  const [proxyAdmins, setProxyAdmins] = useState([]);
  const [proxyAdminErrors, setProxyAdminErrors] = useState([]);

  const [createTenantUpdate] = useCreateTenantUpdateMutation();

  useEffect(() => {
    getNames();
  }, []);

  const getNames = async () => {
    const url = `${serviceUrl}/api/tenant/getTenantNames`;
    try {
      let response = await axios.get(url, { headers: genericHeaders() });
      if (response.status === 200) {
        setNames(response.data);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const handleChange = (evt) => {
    if (evt.target) {
      let newData = { ...formData };
      newData[evt.target.name] = evt.target.value;
      setFormData(newData);
    }
  };

  const handleCheckError = async (evt) => {
    let newData = { ...error };
    if (evt.target.value === "") {
      if (evt.target.name === "tenantName") {
        newData.tenantName = "Please Enter Tenant Name";
      } else if (evt.target.name === "email") {
        newData.email = "Please enter your email";
      } else if (evt.target.name === "address") {
        newData.address = "Please Enter Full Address";
      }
    } else if (evt.target.name === "tenantName") {
      let tenantNames = [...names];
      tenantNames.every((item) => {
        if (item.toLowerCase() === evt.target.value.toLowerCase()) {
          newData.tenantName = "Name is already in use";
          return false;
        } else {
          newData.tenantName = "";
        }
        return true;
      });
    } else if (evt.target.name === "email" && !emailPattern.test(evt.target.value)) {
      newData.email = "Please enter a valid email address";
    } else {
      newData[evt.target.name] = "";
    }
    setError(newData);
  };

  const handlePhoneCheck = (value) => {
    let newData = { ...error };
    const validMobile = validateMobileNumber(value);
    if (value === "") {
      newData.phoneNumber = "Please enter mobile number";
    } else {
      newData.phoneNumber = !validMobile ? "Please enter valid mobile number" : "";
    }
    setError(newData);
  };

  // Proxy admin handlers
  const handleProxyAdminChange = (index, field, value) => {
    const updated = proxyAdmins.map((admin, i) =>
      i === index ? { ...admin, [field]: value } : admin
    );
    setProxyAdmins(updated);
  };

  const handleProxyAdminBlur = (index, field, value) => {
    const updatedErrors = proxyAdminErrors.map((err, i) => {
      if (i !== index) return err;
      const newErr = { ...err };
      if (!value.trim()) {
        if (field === "name") newErr.name = "Please enter admin name";
        else if (field === "emailId") newErr.emailId = "Please enter admin email";
        else if (field === "phoneNumber") newErr.phoneNumber = "Please enter admin mobile";
      } else if (field === "emailId" && !emailPattern.test(value)) {
        newErr.emailId = "Please enter a valid email address";
      } else {
        newErr[field] = "";
      }
      return newErr;
    });
    setProxyAdminErrors(updatedErrors);
  };

  const handleProxyAdminMobileBlur = (index, value) => {
    const updatedErrors = proxyAdminErrors.map((err, i) => {
      if (i !== index) return err;
      const newErr = { ...err };
      if (!value) {
        newErr.phoneNumber = "Please enter admin mobile";
      } else if (!validateMobileNumber(value)) {
        newErr.phoneNumber = "Please enter a valid mobile number";
      } else {
        newErr.phoneNumber = "";
      }
      return newErr;
    });
    setProxyAdminErrors(updatedErrors);
  };

  const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  const addProxyAdmin = () => {
    setProxyAdmins([...proxyAdmins, emptyProxyAdmin()]);
    setProxyAdminErrors([...proxyAdminErrors, emptyProxyAdminError()]);
  };

  const removeProxyAdmin = (index) => {
    setProxyAdmins(proxyAdmins.filter((_, i) => i !== index));
    setProxyAdminErrors(proxyAdminErrors.filter((_, i) => i !== index));
  };

  const isProxyAdminsFilled = () =>
    proxyAdmins.every(
      (a, i) =>
        a.name.trim() !== "" &&
        a.emailId.trim() !== "" &&
        emailPattern.test(a.emailId) &&
        a.phoneNumber !== "" &&
        (proxyAdminErrors[i]?.phoneNumber ?? "") === ""
    );

  const isFormValid =
    formData.tenantName !== "" &&
    formData.email !== "" &&
    formData.phoneNumber !== "" &&
    formData.address !== "" &&
    formData.tenantCountry !== "" &&
    error.email === "" &&
    error.phoneNumber === "" &&
    error.tenantName === "" &&
    error.address === "" &&
    (proxyAdmins.length === 0 || isProxyAdminsFilled());

  const handleSuccessSnackOpen = (open, error, message) => {
    setSuccessSnackbarOpen({ open, error, message });
  };

  const handleSucessSnackbarClose = () => {
    setSuccessSnackbarOpen({ open: false, error: false, message: "" });
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = { ...formData, proxyAdmins: proxyAdmins.length > 0 ? proxyAdmins : [] };
      const response = await createTenantUpdate({ payload });
      if (response.error.originalStatus === 201) {
        handleSuccessSnackOpen(true, false, "Tenant Created successfully");
        setIsDialogOpen(true);
        localStorage.setItem("tenantLogoUrl", formData.logoUrl || "");
localStorage.setItem("tenantName", formData.tenantName || "");
        setFormData({
          tenantName: "",
          description: "",
          email: "",
          tenantCountry: "",
          phoneNumber: "",
          logoUrl: "",
          createdDate: "",
          address: "",
        });
        setProxyAdmins([]);
        setProxyAdminErrors([]);
      } else {
        handleSuccessSnackOpen(true, true, "Failed to update tenant");
      }
    } catch (error) {
      console.error("Error updating tenant:", error);
      handleSuccessSnackOpen(true, true, "An error occurred while updating tenant");
    } finally {
      setLoading(false);
    }
  };

  // Progress indicator (just visual)
  const filledTenantFields = [
    formData.tenantName,
    formData.email,
    formData.tenantCountry,
    formData.phoneNumber,
    formData.address,
  ].filter((v) => v && v !== "").length;
  const totalTenantFields = 5;
  const progressPct = Math.round((filledTenantFields / totalTenantFields) * 100);

  return (
    <>
      {loading && <Loader />}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} PaperProps={{ sx: { borderRadius: 3, padding: 1, minWidth: 380 } }}>
        <DialogTitle className="!font-bold !text-slate-800 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-secondery-1 text-blue-primary">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          </span>
          Tenant Created Successfully
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained" sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, backgroundColor: "#60A5FA", "&:hover": { backgroundColor: "#3B82F6" } }}>Close</Button>
        </DialogActions>
      </Dialog>

      <div className="px-8 py-7 bg-blue-secondery-2 min-h-[calc(100vh-64px)]">
        {/* ── Page header ── */}
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-secondery-1 text-blue-primary text-[11px] font-bold uppercase tracking-wider mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-primary animate-pulse" />
              Onboarding
            </div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Onboard a New Tenant</h1>
            <p className="text-sm text-gray-primary mt-1 max-w-2xl">
              Register a new organization on the platform and add the primary contact details. You can optionally add proxy admins to manage the workspace.
            </p>
          </div>
          {/* Progress */}
          <div className="bg-white rounded-2xl border border-blue-secondery px-5 py-4 shadow-sm w-full md:w-[280px]">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-primary mb-2">
              <span>Form Completion</span>
              <span className="text-blue-primary">{progressPct}%</span>
            </div>
            <div className="h-2 w-full bg-blue-secondery-1 rounded-full overflow-hidden">
              <div className="h-full bg-blue-primary transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="text-[11px] text-gray-primary mt-2">{filledTenantFields} of {totalTenantFields} required fields filled</p>
          </div>
        </div>

        {/* ── Two-column form layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left column — Tenant Details */}
          <section className="lg:col-span-7 bg-white rounded-2xl border border-blue-secondery shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-blue-secondery-1 bg-blue-secondery-2 flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-blue-primary text-white flex items-center justify-center shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </span>
              <div>
                <h2 className="text-base font-bold text-slate-800 leading-tight">Tenant Details</h2>
                <p className="text-[11px] text-gray-primary">Organization profile and primary contact</p>
              </div>
            </div>

            <div className="p-6">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-primary mb-3">Identity</p>
              <div className="flex flex-col gap-1 mb-5">
                <TextField
                  name="tenantName"
                  label="Tenant Name"
                  type="text"
                  value={formData.tenantName}
                  onChange={handleChange}
                  onBlur={(e) => handleCheckError(e)}
                  error={error.tenantName}
                  isMandatory={true}
                />
                <TextField
                  name="description"
                  label="Description"
                  type="text"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-primary mb-3">Contact</p>
              <div className="flex flex-col gap-1 mb-5">
                <TextField
                  name="email"
                  label="Email"
                  type="text"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={(e) => handleCheckError(e)}
                  error={error.email}
                  isMandatory={true}
                />
                <Select
                  name="tenantCountry"
                  label="Country"
                  options={[
                    { value: "INDIA", label: "INDIA" },
                    { value: "USA", label: "USA" },
                    { value: "OTHER", label: "OTHER" },
                  ]}
                  value={formData.tenantCountry}
                  onChange={handleChange}
                  isMandatory={true}
                />
                <div className="flex items-start gap-3 mb-1 mt-2">
                  <p className="text-sm font-semibold text-slate-700 mt-2 whitespace-nowrap min-w-[140px]">
                    Phone Number <span className="text-rose-500 text-base">*</span>
                  </p>
                  <CustomPhoneInput
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onPhoneInputChange={handleChange}
                    customPlaceholder="Enter Phone Number"
                    isMandatory={true}
                    onBlurEvent={handlePhoneCheck}
                    error={error.phoneNumber}
                  />
                </div>
              </div>

              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-primary mb-3">Branding & Address</p>
              <TextField
                name="logoUrl"
                label="Logo URL"
                type="text"
                value={formData.logoUrl}
                onChange={handleChange}
              />
              <p className="bg-blue-secondery-2 border border-blue-secondery rounded-md px-3 py-1.5 text-[11px] text-blue-primary -mt-2 mb-4 ml-1 inline-flex items-center gap-1.5 w-fit font-medium">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Please provide an AWS hosted URL
              </p>

              <div className="flex items-start gap-3 mb-1">
                <label
                  htmlFor="address"
                  className="block text-sm font-semibold text-slate-700 mt-2 whitespace-nowrap min-w-[140px]"
                >
                  Address <span className="text-rose-500 text-base">*</span>
                </label>
                <div className="flex flex-col flex-1">
                  <textarea
                    className="border border-blue-secondery w-full px-3.5 py-2.5 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary text-sm bg-blue-secondery-2 hover:bg-white focus:bg-white transition-colors resize-none placeholder:text-gray-primary"
                    name="address"
                    id="address"
                    rows={3}
                    placeholder="Enter the complete office address"
                    value={formData.address}
                    onChange={handleChange}
                    onBlur={(e) => handleCheckError(e)}
                  />
                  {error.address && (
                    <p className="text-rose-500 text-xs mt-1.5 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rose-500"></span>{error.address}</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Right column — Proxy Admins */}
          <section className="lg:col-span-5 bg-white rounded-2xl border border-blue-secondery shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-blue-secondery-1 bg-blue-secondery-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-blue-primary text-white flex items-center justify-center shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                </span>
                <div>
                  <h2 className="text-base font-bold text-slate-800 leading-tight">Proxy Admins</h2>
                  <p className="text-[11px] text-gray-primary">{proxyAdmins.length} configured (optional)</p>
                </div>
              </div>
              <button
                type="button"
                onClick={addProxyAdmin}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-blue-primary rounded-lg px-3 py-2 hover:bg-blue-500 transition-colors shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                Add Admin
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4 flex-1">
              {proxyAdmins.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-gray-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-3 text-blue-secondery" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  <p className="text-sm font-medium">No proxy admins added</p>
                  <p className="text-xs mt-1">Click "Add Admin" to assign proxy admins (optional)</p>
                </div>
              )}
              {proxyAdmins.map((admin, index) => (
                <div
                  key={index}
                  className="border border-blue-secondery-1 rounded-xl p-4 bg-blue-secondery-2 hover:border-blue-primary/40 transition-colors relative"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-slate-700 inline-flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-primary text-white text-xs font-bold shadow-sm">{index + 1}</span>
                      Admin {index + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeProxyAdmin(index)}
                      className="text-xs font-medium text-rose-600 bg-white border border-rose-200 rounded-md px-2.5 py-1 hover:bg-rose-50 hover:border-rose-300 transition-colors inline-flex items-center gap-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      Remove
                    </button>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-[10px] font-bold text-gray-primary uppercase tracking-wider block mb-1.5">
                        Full Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="border border-blue-secondery rounded-lg px-3.5 py-2.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary bg-white placeholder:text-gray-primary transition-colors"
                        placeholder="Jane Doe"
                        value={admin.name}
                        onChange={(e) =>
                          handleProxyAdminChange(index, "name", e.target.value)
                        }
                        onBlur={(e) =>
                          handleProxyAdminBlur(index, "name", e.target.value)
                        }
                      />
                      {proxyAdminErrors[index]?.name && (
                        <p className="text-rose-500 text-xs mt-1.5 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rose-500"></span>
                          {proxyAdminErrors[index].name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-gray-primary uppercase tracking-wider block mb-1.5">
                        Email <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        className="border border-blue-secondery rounded-lg px-3.5 py-2.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary bg-white placeholder:text-gray-primary transition-colors"
                        placeholder="jane@company.com"
                        value={admin.emailId}
                        onChange={(e) =>
                          handleProxyAdminChange(index, "emailId", e.target.value)
                        }
                        onBlur={(e) =>
                          handleProxyAdminBlur(index, "emailId", e.target.value)
                        }
                      />
                      {proxyAdminErrors[index]?.emailId && (
                        <p className="text-rose-500 text-xs mt-1.5 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rose-500"></span>
                          {proxyAdminErrors[index].emailId}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-gray-primary uppercase tracking-wider block mb-1.5">
                        Mobile <span className="text-rose-500">*</span>
                      </label>
                      <CustomPhoneInput
                        name={`phoneNumber_${index}`}
                        value={admin.phoneNumber}
                        onPhoneInputChange={(e) =>
                          handleProxyAdminChange(index, "phoneNumber", e.target.value)
                        }
                        customPlaceholder="Enter mobile number"
                        onBlurEvent={(value) => handleProxyAdminMobileBlur(index, value)}
                        error={proxyAdminErrors[index]?.phoneNumber || ""}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* ── Sticky submit bar ── */}
        <div className="mt-6 bg-white rounded-2xl border border-blue-secondery shadow-sm px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`w-2.5 h-2.5 rounded-full ${isFormValid ? "bg-emerald-500 animate-pulse" : "bg-gray-primary"}`} />
            <p className="text-sm text-slate-700">
              {isFormValid ? (
                <span className="font-semibold text-emerald-600">Ready to submit</span>
              ) : (
                <span className="text-gray-primary">Fill all required fields to enable submission</span>
              )}
            </p>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`px-7 py-2.5 rounded-xl font-bold text-sm transition-all duration-200 inline-flex items-center gap-2 ${
              isFormValid
                ? "bg-blue-primary text-white hover:bg-blue-500 shadow-md shadow-blue-secondery hover:shadow-lg hover:-translate-y-0.5"
                : "bg-blue-secondery-1 text-gray-primary cursor-not-allowed"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            Create Tenant
          </button>
        </div>

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          open={successSnackbarOpen.open}
          autoHideDuration={2000}
          onClose={handleSucessSnackbarClose}
        >
          <Alert
            severity={successSnackbarOpen.error ? "error" : "success"}
            variant="filled"
            onClose={handleSucessSnackbarClose}
          >
            {successSnackbarOpen.message}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default TenantForm;

export const CustomPhoneInput = ({
  name,
  value,
  onPhoneInputChange,
  customPlaceholder,
  onBlurEvent,
  error,
}) => {
  return (
    <div className="flex flex-col">
      <PhoneInput
        inputClass="!border !border-blue-secondery !rounded-lg !py-2 !pr-2 !pl-12 !text-sm focus:!outline-none focus:!border-blue-primary focus:!ring-2 focus:!ring-blue-primary !w-full"
        className="mb-1 ml-0"
        containerStyle={{ width: "100%", maxWidth: "16rem" }}
        inputStyle={{ height: 42, borderRadius: 8, width: "100%" }}
        buttonStyle={{ borderTopLeftRadius: 8, borderBottomLeftRadius: 8, borderColor: "#BFDBFE", backgroundColor: "#F0F9FF" }}
        dropdownStyle={{ borderRadius: 8 }}
        country={"in"}
        value={value}
        onChange={(formattedValue) =>
          onPhoneInputChange({ target: { name: name, value: formattedValue } })
        }
        placeholder={customPlaceholder}
        onBlur={(e) => onBlurEvent(e.target.value)}
      />
      {error !== "" && (
        <p className="text-rose-500 text-xs mt-1 ml-1 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rose-500"></span>{error}</p>
      )}
    </div>
  );
};
